/**
 * Stunden zusammenfassen, wenn 24 Balken nicht in die Breite passen.
 *
 * **Auf einem Telefon ist ein Stundenbalken zehn Bildpunkte breit** — bei drei
 * Größen nebeneinander drei. Lesen lässt sich das nicht, antippen auch nicht.
 * Das Diagramm fasst deshalb auf schmaler Fläche je zwei, drei, vier oder sechs
 * Stunden zu einem Balken zusammen, nach der **Uhr der Anlage** (0–3, 3–6 Uhr …;
 * `startOfLocalHourBlock` aus `@hausmeter/timeseries`, Regel 3).
 *
 * **Nur, wenn der Aufrufer es erlaubt** (`ChartProps.mergeHours`). Ob sich zwei
 * Werte addieren lassen, weiß das Diagramm nicht: Zwei Energiemengen ergeben
 * eine, zwei Zählerstände oder zwei Autarkiegrade nicht. Die Bibliothek zeichnet,
 * sie entscheidet nicht — die Freigabe ist die Aussage „diese Balken sind Mengen".
 *
 * Dabei gilt, was für jede Verdichtung gilt:
 *
 * - **Eine Teilsumme bleibt eine Teilsumme** (HM-105). Fehlen in einem Block
 *   Stunden, trägt er den Anteil, der vorliegt, als `coverage` und wird blasser
 *   gezeichnet; ein Block ganz ohne Wert ist eine Lücke (Regel 8).
 * - **Ein Ersatzwert färbt den Block** (HM-115): Der Status ist der unsicherste
 *   seiner Stunden.
 * - **Eine Linie auf der rechten Achse wird gemittelt**, nicht summiert — das
 *   ist der Ladezustand (HM-137), und auch der Server bildet ihn gröber als
 *   Mittel.
 *
 * Reine Rechnung, ohne uPlot und ohne React.
 */
import {
  startOfLocalHourBlock,
  startOfNextLocalHourBlock,
} from "@hausmeter/timeseries";
import type { ChartBucket, MergedHours } from "./buckets.js";
import type { ChartPoint, ChartProps, ChartSeries, PointQuality } from "./types.js";

const HOUR = 3_600_000;

/** Die Blocklängen, in dieser Reihenfolge versucht — alle teilen einen Tag ohne Rest. */
const BLOCKS: readonly (1 | 2 | 3 | 4 | 6)[] = [1, 2, 3, 4, 6];

const BUCKET_OF: Record<2 | 3 | 4 | 6, MergedHours> = { 2: "2h", 3: "3h", 4: "4h", 6: "6h" };

/**
 * So breit muss ein Abschnitt mindestens sein. Ein einzelner Balken braucht
 * Platz für einen Finger, nebeneinanderstehende je Reihe eine erkennbare Breite
 * — auf einem schmalen Abschnitt füllen sie 80 % davon (`options.ts`).
 */
const MIN_SLOT_PX = 18;
const MIN_BAR_PX = 10;

/** Was links neben der Zeichenfläche liegt — die Wertachse samt Beschriftung. */
const AXIS_PX = 56;
/** Was rechts liegt: die Achse des Ladezustands („100", die Einheit steht darüber), sonst der Rand für die letzte Uhrzeit. */
const RIGHT_AXIS_PX = 28;
const RIGHT_PADDING_PX = 26;

/**
 * Wie viele Stunden ein Balken bei dieser Breite fasst — `1`, wenn 24
 * Stundenbalken Platz haben oder nicht zusammengefasst werden darf.
 */
export function hoursPerBar(props: ChartProps, width: number): 1 | 2 | 3 | 4 | 6 {
  if (props.mergeHours !== true || props.bucket !== "1h" || width <= 0) return 1;
  const left = props.series.filter((entry) => (entry.axis ?? "left") === "left");
  if (left.length === 0 || left.some((entry) => entry.mark !== "bars")) return 1;

  const hours = Math.max(1, Math.round((props.to - props.from) / HOUR));
  const side = props.stacked === true ? 1 : left.length;
  const slot = Math.max(MIN_SLOT_PX, side * MIN_BAR_PX);
  const right = props.series.some((entry) => entry.axis === "right") ? RIGHT_AXIS_PX : RIGHT_PADDING_PX;
  const plot = Math.max(1, width - AXIS_PX - right);
  return BLOCKS.find((block) => plot / Math.ceil(hours / block) >= slot) ?? 6;
}

const RANK: Record<PointQuality, number> = { measured: 0, interpolated: 1, estimated: 2, suspect: 3 };

/** Die Punkte einer Reihe, je lokalem Block zusammengefasst — summiert oder gemittelt. */
export function mergePoints(
  points: readonly ChartPoint[],
  hours: 2 | 3 | 4 | 6,
  timeZone: string,
  combine: "sum" | "mean",
): ChartPoint[] {
  const groups = new Map<number, ChartPoint[]>();
  for (const point of points) {
    const start = startOfLocalHourBlock(new Date(point.at), hours, timeZone).getTime();
    const group = groups.get(start);
    if (group === undefined) groups.set(start, [point]);
    else group.push(point);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([start, group]) => {
      /* Im Herbst kommt die Stunde 2–3 zweimal vor; liegt der errechnete
         Blockbeginn auf der zweiten, beginnt der Balken trotzdem mit der ersten. */
      const at = Math.min(start, ...group.map((point) => point.at));
      const present = group.filter(
        (point): point is ChartPoint & { value: number } => point.value !== null,
      );
      if (present.length === 0) return { at, value: null };

      const quality = present.reduce<PointQuality>((worst, point) => {
        const own = point.quality ?? "measured";
        return RANK[own] > RANK[worst] ? own : worst;
      }, "measured");
      const status = quality === "measured" ? {} : { quality };

      if (combine === "mean") {
        const value = present.reduce((sum, point) => sum + point.value, 0) / present.length;
        return { at, value, ...status };
      }

      const value = present.reduce((sum, point) => sum + point.value, 0);
      /* Die Abdeckung gegen die **wirkliche** Länge des Blocks: am
         Umstellungstag zwei oder vier Stunden statt drei. */
      const end = startOfNextLocalHourBlock(new Date(start), hours, timeZone).getTime();
      const span = Math.max(1, Math.round((end - at) / HOUR));
      const covered = present.reduce((sum, point) => sum + (point.coverage ?? 1), 0) / span;
      return { at, value, ...(covered < 0.9995 ? { coverage: covered } : {}), ...status };
    });
}

/**
 * Die Eingaben des Diagramms für diese Breite — unverändert, wenn nichts
 * zusammengefasst wird, sonst mit Blöcken statt Stunden und dem passenden Raster.
 */
export function mergeForWidth(props: ChartProps, width: number): ChartProps {
  const hours = hoursPerBar(props, width);
  if (hours === 1) return props;
  const bucket: ChartBucket = BUCKET_OF[hours];
  return {
    ...props,
    bucket,
    step: undefined,
    series: props.series.map(
      (entry): ChartSeries => ({
        ...entry,
        points: mergePoints(
          entry.points,
          hours,
          props.timeZone,
          (entry.axis ?? "left") === "right" ? "mean" : "sum",
        ),
      }),
    ),
  };
}
